import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../core/config.service';
import { Injectable } from '@angular/core';
import { ClimateMetaData } from 'app/core/climate-readings.service';

export interface Room {
  room_name: string;
  room_id: string;
  location_id?: string;
  template_id?: string;
  created_by?: string;
  create_time?: number;
  last_update_time?: number;
}

export interface ViewRoom {
  room_name: string;
  room_id: string;
  door_statuses?: Array<DoorStatus>;
  occupancy_status?: boolean;
  door_status?: number; // 0 is open
  aircon_statuses?: Array<AirconStatus>;
  has_automation_setting_overrides?: boolean;
}

export interface AirconStatus {
  automationMode: string;
  nodeName: string;
  automationsetId: string;
  automationStatus: string;
  dewPoint?: number;
  ambTemp: number;
  pressure?: number;
  humidity?: number;
  mode: number;
  powered: boolean;
  fanSpeed: number;
  temperature: number;
}

export interface DoorStatus {
  nodeName: string;
  doorStatus: number;
}

// this is subset of the data found in energy_nodes, occupancy_nodes etc.
export interface NodeUpdates {
  node_id: string;
  data_status: Array<DoorStreamData | AirconStreamData | OccupancyStreamData>;
}

// basic interface common to all other node interfaces, for enable automatic narrowing type conversion
export interface RoomBasicNode {
  node_id: string;
  template_node_id: string;
  name: string;
  automation_set_id: string;
}

export interface RoomGenericNode {
  automation_set_id: string;
  template_node_id: string;
  create_time: number;
  location_id: string;
  last_updated_by: string;
  description?: string;
  name: string;
  room_id: string;
  node_id: string;
  last_update_time: number;
  type: string;
}

export interface RoomOccupancyNode {
  node_id: string;
  template_node_id: string;
  name: string;
  automation_set_id: string;
  data_status: OccupancyStreamData[];
}

export interface RoomDoorNode {
  node_id: string;
  template_node_id: string;
  name: string;
  automation_set_id: string;
  data_status: DoorStreamData[];
}

export interface RoomAirconNode {
  node_id: string;
  template_node_id: string;
  name: string;
  automation_set_id: string;
  data_status: AirconStreamData[];
}

export interface RoomsRenderData {
  rooms: RoomData[];
  autosets_with_template_ids: AutosetWithTemplateIds;
}

export interface AutosetWithTemplateIds {
  // map of autoset_ids => template_autoset_id, expect to be {} unless created with V4 templates
  [key: string]: string;
}

export interface RoomData {
  room_name: string;
  room_id: string;
  template_id: string;
  meta_climate?: ClimateMetaData;
  energy_nodes: Array<{
    node_id: string;
    template_node_id: string;
    name: string;
    automation_set_id: string;
    // data_status: StreamData[]
  }>;
  occupancy_nodes: Array<RoomOccupancyNode>;
  door_nodes: Array<RoomDoorNode>;
  aircon_nodes: Array<RoomAirconNode>;
}

export interface DoorStreamData {
  stream_type: string;
  measurement_timestamp: number;
  type: string;
  stream_id: string;
  open: boolean;
}

export interface OccupancyStreamData {
  measurement_timestamp: number;
  occupancy: boolean;
  stream_id: string;
  temperature_ambient?: number;
  // temperature_wall: number,
  type: string;
}

export interface AirconStreamData {
  fan_speed: number;
  measurement_timestamp: number;
  mode: number;
  automation_status: string;
  powered: boolean;
  stream_id: string;
  temperature_set: number;
  type: string;
  vanne: number;
  // temperature_ambient
  temperature_ambient?: number;
  // humidity measurements
  humidity_ambient?: number;
  // dew point
  dew_point?: number;
  // pressure ambient
  pressure_ambient?: number;
}

export interface AutomationModeData {
  location_id: string;
  automation_mode: string;
  exceptions: Array<{
    automation_set_id: string;
    room_id: string;
    automation_mode: string;
  }>;
}

@Injectable()
export class RoomService {
  constructor(private config: ConfigService, private httpClient: HttpClient) {}

  getAllRoomsP(locationId) {
    const url = `${this.config.getRoomUrl()}/rooms/list/${locationId}`;
    return this.httpClient.get<Room[]>(url).toPromise();
  }

  getAllNodesForRoom(room_id) {
    const url = `${this.config.getNodeUrl()}/nodes/room/${room_id}`;
    return this.httpClient.get<RoomGenericNode[]>(url).toPromise();
  }

  getAllRoomsRenderData(locationId) {
    const url = `${this.config.getRoomUrl()}/rooms/list/${locationId}/room-data`;
    return this.httpClient.get<RoomsRenderData>(url).toPromise();
  }

  getAllUpdatedNodes(locationId, unixTimestamp) {
    const url = `${this.config.getRoomUrl()}/rooms/list/${locationId}/updated-nodes/${unixTimestamp} `;
    return this.httpClient.get<NodeUpdates[]>(url).toPromise();
  }

  getAllRoomsAutomodeExceptions(locationId) {
    const url = `${this.config.getRoomUrl()}/rooms/list/${locationId}/automation-mode`;
    return this.httpClient.get<AutomationModeData>(url).toPromise();
  }

  getUpdatedAutomodeExceptions(locationId, jsTimestamp) {
    const url = `${this.config.getRoomUrl()}/rooms/list/${locationId}/automation-mode/${jsTimestamp}`;
    return this.httpClient.get<AutomationModeData>(url).toPromise();
  }

  getRoomByIdP(roomId) {
    const url = `${this.config.getRoomUrl()}/rooms/${roomId}`;
    return this.httpClient.get<Room>(url).toPromise();
  }
}



// WEBPACK FOOTER //
// /Users/sensorflow/Documents/legacy/kalinda/Dashboard/sensorflow-dashboard/src/app/locations/room.service.ts